/**
 * FunctionalAreaDashboard
 *
 * Reusable template component that renders any functional area dashboard
 * from a FunctionalAreaConfig object. Adding a new area requires only
 * a config entry in functionalAreaConfigs.ts — no new components.
 *
 * Layout: header with icon, KPI cards, quick actions, sub-page grid,
 *         recent activity placeholder.
 *
 * WQ-087a: Functional Area Dashboard Template
 */

import React from 'react';
import { resolveIcon } from '../../../components/common/IconResolver';
import type { FunctionalAreaConfig, KPIConfig, QuickAction, SubPageLink } from '../config/functionalAreaConfigs';

interface FunctionalAreaDashboardProps {
  config: FunctionalAreaConfig;
}

// ---------------------------------------------------------------------------
// KPI Card (reuses pattern from TenantUserLanding)
// ---------------------------------------------------------------------------

const AreaKPICard: React.FC<{ kpi: KPIConfig }> = ({ kpi }) => {
  const changeColor = kpi.changeDirection === 'up'
    ? 'var(--success, #22c55e)'
    : kpi.changeDirection === 'down'
      ? 'var(--danger, #ef4444)'
      : 'var(--text-muted, #64748b)';

  return (
    <div className="card stat-card card-elevated" data-testid={`kpi-${kpi.id}`}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--space-3, 12px)' }}>
        <div style={{
          width: '48px',
          height: '48px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'var(--accent-primary-muted, rgba(124, 58, 237, 0.1))',
          borderRadius: 'var(--radius-lg, 12px)',
          color: 'var(--accent-primary, #7c3aed)',
          flexShrink: 0,
        }}>
          {resolveIcon(kpi.icon, 24)}
        </div>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div className="stat-value" style={{ fontSize: 'var(--text-2xl, 24px)' }}>
            {kpi.value}
          </div>
          <div className="stat-label">{kpi.label}</div>
          {kpi.change && (
            <div style={{
              fontSize: '12px',
              color: changeColor,
              marginTop: '2px',
              fontWeight: 500,
            }}>
              {kpi.changeDirection === 'up' ? '↑' : kpi.changeDirection === 'down' ? '↓' : '→'}{' '}
              {kpi.change}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Quick Action Button (reuses pattern from TenantUserLanding)
// ---------------------------------------------------------------------------

const AreaQuickAction: React.FC<{ action: QuickAction }> = ({ action }) => {
  const handleClick = () => {
    window.dispatchEvent(new CustomEvent('admin-navigate', { detail: { tab: action.viewKey } }));
  };

  return (
    <button
      className="btn"
      onClick={handleClick}
      data-testid={`quick-action-${action.viewKey}`}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '12px 20px',
        background: 'var(--bg-secondary, rgba(255,255,255,0.05))',
        border: '1px solid var(--border-primary, rgba(255,255,255,0.1))',
        borderRadius: 'var(--radius-md, 8px)',
        color: 'var(--text-primary)',
        cursor: 'pointer',
        fontSize: '14px',
        fontWeight: 500,
        transition: 'background 0.15s, border-color 0.15s',
      }}
      onMouseEnter={e => {
        (e.currentTarget as HTMLElement).style.background = 'var(--bg-hover, rgba(255,255,255,0.08))';
        (e.currentTarget as HTMLElement).style.borderColor = 'var(--accent-primary, #7c3aed)';
      }}
      onMouseLeave={e => {
        (e.currentTarget as HTMLElement).style.background = 'var(--bg-secondary, rgba(255,255,255,0.05))';
        (e.currentTarget as HTMLElement).style.borderColor = 'var(--border-primary, rgba(255,255,255,0.1))';
      }}
    >
      {resolveIcon(action.icon, 18)}
      {action.label}
    </button>
  );
};

// ---------------------------------------------------------------------------
// Sub-Page Card
// ---------------------------------------------------------------------------

const SubPageCard: React.FC<{ link: SubPageLink }> = ({ link }) => {
  const handleClick = () => {
    window.dispatchEvent(new CustomEvent('admin-navigate', { detail: { tab: link.viewKey } }));
  };

  return (
    <button
      onClick={handleClick}
      data-testid={`subpage-${link.viewKey}`}
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 'var(--space-3, 12px)',
        padding: 'var(--space-4, 16px)',
        background: 'var(--bg-secondary, rgba(255,255,255,0.03))',
        border: '1px solid var(--border-primary, rgba(255,255,255,0.08))',
        borderRadius: 'var(--radius-lg, 12px)',
        cursor: 'pointer',
        textAlign: 'left',
        width: '100%',
        transition: 'background 0.15s, border-color 0.15s, transform 0.1s',
        color: 'inherit',
      }}
      onMouseEnter={e => {
        (e.currentTarget as HTMLElement).style.background = 'var(--bg-hover, rgba(255,255,255,0.06))';
        (e.currentTarget as HTMLElement).style.borderColor = 'var(--accent-primary, #7c3aed)';
      }}
      onMouseLeave={e => {
        (e.currentTarget as HTMLElement).style.background = 'var(--bg-secondary, rgba(255,255,255,0.03))';
        (e.currentTarget as HTMLElement).style.borderColor = 'var(--border-primary, rgba(255,255,255,0.08))';
      }}
    >
      <div style={{
        width: '40px',
        height: '40px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--accent-primary-muted, rgba(124, 58, 237, 0.1))',
        borderRadius: 'var(--radius-md, 8px)',
        color: 'var(--accent-primary, #7c3aed)',
        flexShrink: 0,
      }}>
        {resolveIcon(link.icon, 20)}
      </div>
      <div style={{ minWidth: 0 }}>
        <div style={{
          fontSize: '14px',
          fontWeight: 600,
          color: 'var(--text-primary)',
          marginBottom: '4px',
        }}>
          {link.label}
        </div>
        <div style={{
          fontSize: '12px',
          color: 'var(--text-secondary, #94a3b8)',
          lineHeight: 1.4,
        }}>
          {link.description}
        </div>
      </div>
    </button>
  );
};

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

const FunctionalAreaDashboard: React.FC<FunctionalAreaDashboardProps> = ({ config }) => {
  return (
    <div className="page-container" data-testid={`area-dashboard-${config.areaKey}`}>
      {/* Area Header */}
      <div className="page-header">
        <h2 className="page-title" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {resolveIcon(config.areaIcon, 28)}
          {config.areaName}
        </h2>
        <p style={{ color: 'var(--text-secondary)', marginTop: '4px', fontSize: '14px' }}>
          {config.description}
        </p>
      </div>

      {/* KPI Cards */}
      <div
        className="grid grid-cols-4"
        style={{ marginBottom: 'var(--space-6, 24px)' }}
        data-testid="kpi-grid"
      >
        {config.kpis.map(kpi => (
          <AreaKPICard key={kpi.id} kpi={kpi} />
        ))}
      </div>

      {/* Quick Actions */}
      <div className="card card-elevated" style={{ padding: 'var(--space-4, 16px)', marginBottom: 'var(--space-6, 24px)' }}>
        <h3 style={{
          margin: '0 0 var(--space-4, 16px)',
          fontSize: 'var(--text-lg, 18px)',
          color: 'var(--text-primary)',
        }}>
          Quick Actions
        </h3>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          {config.quickActions.map(action => (
            <AreaQuickAction key={action.viewKey} action={action} />
          ))}
        </div>
      </div>

      {/* Sub-Pages Grid */}
      <div style={{ marginBottom: 'var(--space-6, 24px)' }}>
        <h3 style={{
          margin: '0 0 var(--space-4, 16px)',
          fontSize: 'var(--text-lg, 18px)',
          color: 'var(--text-primary)',
        }}>
          Explore
        </h3>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
          gap: 'var(--space-3, 12px)',
        }}>
          {config.subPages.map(link => (
            <SubPageCard key={link.viewKey} link={link} />
          ))}
        </div>
      </div>

      {/* Recent Activity Placeholder */}
      <div className="card card-elevated" style={{
        padding: 'var(--space-4, 16px)',
        background: 'var(--bg-secondary, rgba(255,255,255,0.03))',
        borderLeft: '3px solid var(--accent-primary, #7c3aed)',
      }}>
        <h4 style={{ margin: '0 0 8px', fontSize: '14px', color: 'var(--text-primary)' }}>
          Recent Activity
        </h4>
        <p style={{ margin: 0, fontSize: '13px', color: 'var(--text-secondary)', lineHeight: '1.5' }}>
          Activity feed for {config.areaName} will display recent updates, changes, and notifications
          from this functional area once connected to live data sources.
        </p>
      </div>
    </div>
  );
};

export default FunctionalAreaDashboard;
